import React from 'react';
import { Link } from '@/components/LocaleLink';
import { trackEvent } from '@/utils/analytics';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { whyRetailDataFailsCopy } from '@/contexts/language/translations/pages/whyRetailDataFails';

/**
 * Single source of truth for the "Why Traditional Retail Data Fails" content —
 * shared between Solutions.tsx (RAS) and OurWorkRetailManagement.tsx so the
 * heading count, card copy and CTA can never drift out of sync again.
 * Copy (EN + SK) lives in translations/pages/whyRetailDataFails.ts; the problem
 * count word in the intro is derived from the number of problems per language.
 */

interface WhyRetailDataFailsSectionProps {
  /** 'gradient' = RAS-style peach gradient cards; 'numbered' = RPM-style 01–04 numbered cards. */
  variant: 'gradient' | 'numbered';
  /** analytics label passed to the section's own "Tell me more" click. */
  trackingLabel: string;
}

const WhyRetailDataFailsSection = ({ variant, trackingLabel }: WhyRetailDataFailsSectionProps) => {
  const isGradient = variant === 'gradient';
  const { language } = useLanguage();
  const c = whyRetailDataFailsCopy[language];

  return (
    <div className="max-w-5xl mx-auto">
      <h2
        className={
          isGradient
            ? 'text-3xl md:text-4xl font-bold text-center mb-4'
            : 'text-3xl md:text-4xl font-bold text-center text-[#2A1E47] mb-4'
        }
      >
        {isGradient ? (
          <>{c.headingPrefix}<span className="text-[#FF6B35]">{c.headingAccent}</span></>
        ) : (
          c.headingPrefix + c.headingAccent
        )}
      </h2>
      <p
        className={
          isGradient
            ? 'text-foreground/90 text-center text-lg max-w-3xl mx-auto mb-12'
            : 'text-center text-muted-foreground text-lg max-w-2xl mx-auto mb-16'
        }
      >
        {c.intro(c.problems.length)}
      </p>

      <div className={isGradient ? 'grid grid-cols-1 md:grid-cols-2 gap-6' : 'grid grid-cols-1 md:grid-cols-2 gap-8'}>
        {c.problems.map((item, i) =>
          isGradient ? (
            <div
              key={item.title}
              className="rounded-xl p-6 text-left shadow-sm border border-orange-50/60 min-h-full"
              style={{ background: 'linear-gradient(to bottom, #FFECD3 0%, #ffffff 100%)' }}
            >
              <h3 className="font-bold text-foreground text-lg mb-3">{item.title}</h3>
              <p className="text-foreground/90 text-sm leading-relaxed">{item.text}</p>
            </div>
          ) : (
            <div key={item.title} className="rounded-2xl border border-gray-100 bg-white p-7 shadow-sm">
              <span className="text-4xl font-bold text-[#2A1E47]/10 block mb-4">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 className="text-lg font-bold text-[#2A1E47] mb-3">{item.title}</h3>
              <p className="text-sm text-muted-foreground">{item.text}</p>
            </div>
          )
        )}
      </div>

      <div className="text-center mt-12">
        <Link
          to="/tell-me-more"
          onClick={() => trackEvent('tell_me_more_click', 'engagement', trackingLabel)}
          className={
            isGradient
              ? 'inline-flex items-center gap-2 px-8 py-3.5 rounded-lg bg-[#FF6B35] text-white font-semibold text-lg shadow-md hover:bg-[#FF6B35]/90 hover:shadow-lg transition-all'
              : 'inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-[#FF6B35]/30 text-[#FF6B35] font-semibold hover:bg-[#FF6B35]/5 transition-colors'
          }
        >
          {c.cta}
          <svg className={isGradient ? 'w-5 h-5' : 'w-4 h-4'} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
          </svg>
        </Link>
      </div>
    </div>
  );
};

export default WhyRetailDataFailsSection;
